<template>
    <div class="admin-page">
        <!-- Supabase Authentication Screen -->
        <div v-if="!user" class="auth-container">
            <div class="auth-card">
                <h2>Admin Access</h2>
                <p class="auth-subtitle">Sign in with your admin credentials</p>

                <form @submit.prevent="handleLogin" class="auth-form">
                    <div class="input-group">
                        <input
                            v-model="email"
                            type="email"
                            placeholder="Admin email"
                            class="email-input"
                            required
                        />
                    </div>

                    <div class="input-group">
                        <input
                            v-model="password"
                            type="password"
                            placeholder="Password"
                            class="password-input"
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        class="login-button"
                        :disabled="authLoading"
                    >
                        {{ authLoading ? 'Signing in...' : 'Sign In' }}
                    </button>
                </form>

                <div v-if="authError" class="error-message">
                    {{ authError }}
                </div>
            </div>
        </div>

        <!-- Admin Content -->
        <div v-else class="admin-content">
            <div class="admin-header">
                <h1>Guest Management</h1>
                <p class="admin-subtitle">RSVP Status and Dietary Requirements</p>
                <button @click="logout" class="logout-button">Logout</button>
            </div>

        <div class="admin-summary">
            <div class="summary-stats">
                <div class="stat">
                    <span class="stat-number">{{ totalGuests }}</span>
                    <span class="stat-label">Total Guests</span>
                </div>
                <div class="stat">
                    <span class="stat-number">{{ rsvpYesCount }}</span>
                    <span class="stat-label">RSVP Yes</span>
                </div>
                <div class="stat">
                    <span class="stat-number">{{ rsvpNoCount }}</span>
                    <span class="stat-label">RSVP No</span>
                </div>
                <div class="stat">
                    <span class="stat-number">{{ rsvpNotRespondedCount }}</span>
                    <span class="stat-label">Not Responded</span>
                </div>
                <div class="stat">
                    <span class="stat-number">{{ fridayAttendingCount }}</span>
                    <span class="stat-label">Friday Attendance</span>
                </div>
            </div>
        </div>

        <!-- Search and Sorting Controls -->
        <div class="search-sort-controls">
            <div class="search-section">
                <h3>Search Guests</h3>
                <v-text-field
                    v-model="searchTerm"
                    label="Search"
                    variant="outlined"
                    density="compact"
                    clearable
                    class="search-field"
                ></v-text-field>
            </div>

            <div class="sorting-section">
                <h3>Sort by RSVP Status</h3>
                <v-select
                    v-model="rsvpStatusSort"
                    :items="rsvpStatusOptions"
                    label="RSVP Status"
                    variant="outlined"
                    density="compact"
                    clearable
                    class="sort-select"
                ></v-select>
            </div>

            <div class="sorting-section">
                <h3>Sort by Friday Attendance</h3>
                <v-select
                    v-model="fridayStatusSort"
                    :items="fridayStatusOptions"
                    label="Friday Attendance"
                    variant="outlined"
                    density="compact"
                    clearable
                    class="sort-select"
                ></v-select>
            </div>
        </div>

        <div v-if="loading" class="loading">
            <p>Loading guest data...</p>
        </div>

        <div v-else-if="error" class="error">
            <p>Error loading guest data: {{ error }}</p>
        </div>

        <div v-else class="guest-groups">
            <div
                v-for="family in groupedGuests"
                :key="family.groupKey"
                class="family-group"
            >
                <h2 class="family-name" @click="navigateToFamilyInvitation(family.familyId)">
                    {{ family.familyName }}
                    <span v-if="rsvpStatusSort && family.rsvpStatus !== null" class="rsvp-indicator">
                        ({{ getRsvpStatusText(family.rsvpStatus) }})
                    </span>
                </h2>

                <div class="guests-list">
                    <div
                        v-for="guest in family.guests"
                        :key="guest.id"
                        class="guest-card"
                        :class="getRsvpStatusClass(guest.attending)"
                    >
                        <div class="guest-info">
                            <h3 class="guest-name">{{ guest.first_name }} {{ guest.last_name }}</h3>
                            <p class="guest-email">{{ guest.email }}</p>
                        </div>

                        <div class="guest-status">
                            <div class="rsvp-status">
                                <span class="status-label">RSVP:</span>
                                <span
                                    class="status-badge"
                                    :class="getRsvpStatusClass(guest.attending)"
                                >
                                    {{ getRsvpStatusText(guest.attending) }}
                                </span>
                            </div>

                            <div v-if="guest.dietary" class="dietary-info">
                                <span class="dietary-label">Dietary Requirements:</span>
                                <span class="dietary-text">{{ guest.dietary }}</span>
                            </div>

                            <div class="friday-attendance">
                                <span class="friday-label">Friday:&nbsp;</span>
                                <v-switch
                                    v-model="guest.attending_friday"
                                    @change="updateFridayAttendance(guest)"
                                    density="compact"
                                    hide-details
                                    color="green"
                                    class="friday-switch"
                                    :true-value="true"
                                    :false-value="false"
                                    :indeterminate="guest.attending_friday === null"
                                ></v-switch>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import { supabase } from '@/lib/supabaseClient';

export default {
    name: "AdminPage",

    data() {
        return {
            guests: [],
            loading: true,
            error: null,
            // Supabase authentication data
            user: null,
            email: '',
            password: '',
            authLoading: false,
            authError: null,
            // Search and sorting controls
            searchTerm: '',
            rsvpStatusSort: null,
            rsvpStatusOptions: [
                { title: 'Not Responded', value: 'not_responded' },
                { title: 'RSVP No', value: 'no' },
                { title: 'RSVP Yes', value: 'yes' }
            ],
            fridayStatusSort: null,
            fridayStatusOptions: [
                { title: 'Not Set', value: 'not_set' },
                { title: 'Not Attending Friday', value: 'no' },
                { title: 'Attending Friday', value: 'yes' }
            ]
        }
    },

    computed: {
        groupedGuests() {
            let filteredGuests = [...this.guests];

            // Apply search filtering - find families that match the search term
            let matchingFamilyIds = new Set();
            if (this.searchTerm && this.searchTerm.trim()) {
                const searchLower = this.searchTerm.toLowerCase().trim();

                // First, find all families that have at least one matching guest
                this.guests.forEach(guest => {
                    const familyName = (guest.families?.name || guest.family_name || '').toLowerCase();
                    const firstName = (guest.first_name || '').toLowerCase();
                    const lastName = (guest.last_name || '').toLowerCase();
                    const fullName = `${firstName} ${lastName}`.trim();

                    if (familyName.includes(searchLower) ||
                        firstName.includes(searchLower) ||
                        lastName.includes(searchLower) ||
                        fullName.includes(searchLower)) {
                        matchingFamilyIds.add(guest.family_id);
                    }
                });

                // Then filter to include all guests from matching families
                filteredGuests = filteredGuests.filter(guest =>
                    matchingFamilyIds.has(guest.family_id)
                );
            }

            // Apply RSVP status filtering
            if (this.rsvpStatusSort) {
                if (this.rsvpStatusSort === 'not_responded') {
                    filteredGuests = filteredGuests.filter(guest => guest.attending === null);
                } else if (this.rsvpStatusSort === 'no') {
                    filteredGuests = filteredGuests.filter(guest => guest.attending === false);
                } else if (this.rsvpStatusSort === 'yes') {
                    filteredGuests = filteredGuests.filter(guest => guest.attending === true);
                }
            }

            // Apply Friday attendance filtering
            if (this.fridayStatusSort) {
                if (this.fridayStatusSort === 'not_set') {
                    filteredGuests = filteredGuests.filter(guest => guest.attending_friday === null);
                } else if (this.fridayStatusSort === 'no') {
                    filteredGuests = filteredGuests.filter(guest => guest.attending_friday === false);
                } else if (this.fridayStatusSort === 'yes') {
                    filteredGuests = filteredGuests.filter(guest => guest.attending_friday === true);
                }
            }

            // Group guests by family, but handle mixed RSVP responses
            const groups = {};

            filteredGuests.forEach(guest => {
                const familyId = guest.family_id;
                const rsvpStatus = guest.attending;

                // Create a unique key for family + RSVP status combination
                // This ensures families with mixed responses are shown separately
                const groupKey = this.rsvpStatusSort ?
                    `${familyId}_${rsvpStatus}` :
                    familyId;

                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        familyId: familyId,
                        familyName: guest.families?.name || guest.family_name || `Family ${familyId}`,
                        guests: [],
                        rsvpStatus: rsvpStatus,
                        groupKey: groupKey
                    };
                }
                groups[groupKey].guests.push(guest);
            });

            // Convert to array and sort
            let result = Object.values(groups);

            // Apply RSVP status sorting to family groups
            if (this.rsvpStatusSort) {
                result.sort((a, b) => {
                    const statusOrder = { null: 0, false: 1, true: 2 };
                    const aOrder = statusOrder[a.rsvpStatus];
                    const bOrder = statusOrder[b.rsvpStatus];

                    if (aOrder !== bOrder) {
                        return aOrder - bOrder;
                    }

                    // If same RSVP status, sort by family name
                    return a.familyName.localeCompare(b.familyName);
                });
            } else {
                // Default sorting by family name
                result.sort((a, b) => a.familyName.localeCompare(b.familyName));
            }

            return result;
        },

        totalGuests() {
            return this.guests.length;
        },

        rsvpYesCount() {
            return this.guests.filter(guest => guest.attending === true).length;
        },

        rsvpNoCount() {
            return this.guests.filter(guest => guest.attending === false).length;
        },

        rsvpNotRespondedCount() {
            return this.guests.filter(guest => guest.attending === null).length;
        },

        fridayAttendingCount() {
            return this.guests.filter(guest => guest.attending_friday === true).length;
        },

        fridayNotSetCount() {
            return this.guests.filter(guest => guest.attending_friday === null).length;
        }
    },

    async mounted() {
        await this.checkAuthentication();
        if (this.user) {
            await this.fetchGuests();
        }
    },

    methods: {
        async checkAuthentication() {
            // Check if user is already authenticated with Supabase
            const { data: { user } } = await supabase.auth.getUser();
            this.user = user;
        },

        async handleLogin() {
            this.authLoading = true;
            this.authError = null;

            try {
                // Sign in with Supabase
                const { data, error } = await supabase.auth.signInWithPassword({
                    email: this.email,
                    password: this.password
                });

                if (error) {
                    throw error;
                }

                if (data.user) {
                    this.user = data.user;
                    await this.fetchGuests();
                }
            } catch (error) {
                console.error('Auth error:', error);
                this.authError = error.message || 'Invalid email or password. Please try again.';
                this.password = '';
            } finally {
                this.authLoading = false;
            }
        },

        async logout() {
            try {
                await supabase.auth.signOut();
                this.user = null;
                this.email = '';
                this.password = '';
                this.guests = [];
                this.loading = true;
                this.authError = null;
            } catch (error) {
                console.error('Logout error:', error);
            }
        },

        async fetchGuests() {
            try {
                this.loading = true;
                this.error = null;

                const { data, error } = await supabase
                    .from('guests')
                    .select(`
                        *,
                        families!inner(name)
                    `)
                    .order('family_id')
                    .order('first_name');

                if (error) {
                    throw error;
                }

                this.guests = data || [];
            } catch (error) {
                console.error('Error fetching guests:', error);
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        getRsvpStatusText(status) {
            switch (status) {
                case true: return 'Yes';
                case false: return 'No';
                default: return 'Not Responded';
            }
        },

        getRsvpStatusClass(status) {
            switch (status) {
                case true: return 'rsvp-yes';
                case false: return 'rsvp-no';
                default: return 'rsvp-none';
            }
        },

        async updateFridayAttendance(guest) {
            const previousValue = guest.attending_friday;
            try {
                const { error } = await supabase
                    .from('guests')
                    .update({ attending_friday: guest.attending_friday })
                    .eq('id', guest.id);

                if (error) {
                    throw error;
                }
            } catch (error) {
                console.error('Error updating Friday attendance:', error);
                // Revert the change on error
                guest.attending_friday = previousValue;
            }
        },

        navigateToFamilyInvitation(familyId) {
            this.$router.push({
                path: '/',
                query: { id: familyId }
            });
        }
    }
}
</script>

<style scoped>
.admin-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
    font-family: "Playfair", serif;
}

/* Authentication Styles */
.auth-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
    padding: 2rem;
}

.auth-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    padding: 3rem;
    max-width: 400px;
    width: 100%;
    text-align: center;
}

.auth-card h2 {
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 1.8rem;
}

.auth-subtitle {
    color: #666;
    margin-bottom: 2rem;
    font-size: 1rem;
}

.auth-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.input-group {
    position: relative;
}

.email-input, .password-input {
    width: 100%;
    padding: 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    font-family: "Playfair", serif;
    transition: border-color 0.3s ease;
}

.email-input:focus, .password-input:focus {
    outline: none;
    border-color: #1976d2;
}

.login-button {
    background: #1976d2;
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 8px;
    font-size: 1rem;
    font-family: "Playfair", serif;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.login-button:hover:not(:disabled) {
    background: #1565c0;
}

.login-button:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.error-message {
    color: #d32f2f;
    font-size: 0.9rem;
    margin-top: 1rem;
    padding: 0.5rem;
    background: #ffebee;
    border-radius: 4px;
}

.logout-button {
    background: #f44336;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-family: "Playfair", serif;
    cursor: pointer;
    transition: background-color 0.3s ease;
    position: absolute;
    top: 0;
    right: 0;
}

.logout-button:hover {
    background: #d32f2f;
}

.admin-header {
    text-align: center;
    margin-bottom: 3rem;
    position: relative;
}

.admin-header h1 {
    color: #333;
    margin-bottom: 0.5rem;
}

.admin-subtitle {
    color: #666;
    font-size: 1.1rem;
}

.loading, .error {
    text-align: center;
    padding: 3rem;
    font-size: 1.2rem;
}

.error {
    color: #d32f2f;
}

.guest-groups {
    margin-bottom: 3rem;
}

.family-group {
    margin-bottom: 2.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    overflow: hidden;
}

.family-name {
    background: #f8f9fa;
    padding: 1rem 1.5rem;
    margin: 0;
    border-bottom: 1px solid #e0e0e0;
    color: #333;
    font-size: 1.3rem;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    user-select: none;
}

.family-name:hover {
    background: #e9ecef;
    color: #1976d2;
}

.guests-list {
    padding: 1rem;
}

.guest-card {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
    border-left: 4px solid #ddd;
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.guest-card:last-child {
    margin-bottom: 0;
}

.guest-info {
    flex: 1;
}

.guest-name {
    margin: 0 0 0.5rem 0;
    color: #333;
    font-size: 1.1rem;
}

.guest-email {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
}

.guest-status {
    text-align: right;
    min-width: 200px;
}

.rsvp-status {
    margin-bottom: 0.5rem;
}

.status-label {
    font-weight: 500;
    color: #666;
    margin-right: 0.5rem;
}

.status-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.rsvp-yes {
    background: #e8f5e8;
    color: #2e7d32;
    border-left-color: #4caf50;
}

.rsvp-no {
    background: #ffebee;
    color: #c62828;
    border-left-color: #f44336;
}

.rsvp-none {
    background: #f5f5f5;
    color: #666;
    border-left-color: #9e9e9e;
}

.dietary-info {
    font-size: 0.9rem;
}

.dietary-label {
    color: #666;
    margin-right: 0.5rem;
}

.dietary-text {
    color: #333;
    font-style: italic;
}

.friday-attendance {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.friday-label {
    color: #666;
    font-size: 0.9rem;
    font-weight: 500;
}

.friday-switch {
    margin: 0;
    padding: 0;
}

.friday-switch .v-switch__track {
    height: 20px;
}

.friday-switch .v-switch__thumb {
    width: 16px;
    height: 16px;
}

.admin-summary {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 2rem;
    margin-top: 2rem;
    margin-bottom: 2rem;
}

.summary-stats {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
    gap: 2rem;
}

.stat {
    text-align: center;
}

.stat-number {
    display: block;
    font-size: 2.5rem;
    font-weight: bold;
    color: #1976d2;
    margin-bottom: 0.5rem;
}

.stat-label {
    color: #666;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Search and Sorting Controls */
.search-sort-controls {
    background: #fff;
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
}

.search-section {
    flex: 2;
    min-width: 300px;
}

.sorting-section {
    flex: 1;
    min-width: 250px;
}

.search-section h3,
.sorting-section h3 {
    color: #333;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 500;
}

.search-field,
.sort-select {
    width: 100%;
}

.rsvp-indicator {
    font-size: 0.9rem;
    font-weight: normal;
    color: #666;
    margin-left: 0.5rem;
}

/* Responsive design */
@media (max-width: 768px) {
    .admin-page {
        padding: 1rem;
    }

    .guest-card {
        flex-direction: column;
        align-items: flex-start;
    }

    .guest-status {
        text-align: left;
        margin-top: 1rem;
        min-width: auto;
    }

    .friday-attendance {
        justify-content: flex-start;
    }

    .summary-stats {
        gap: 1rem;
    }

    .stat-number {
        font-size: 2rem;
    }

    .search-sort-controls {
        flex-direction: column;
        gap: 1rem;
    }

    .search-section,
    .sorting-section {
        min-width: auto;
    }
}
</style>
